import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, IconButton, Box, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useDispatch } from 'react-redux';
import { getDefectPhotos } from '../redux/slice';
import './PhotoGalleryModal.scss';

const PhotoGalleryModal = ({ open, onClose, photos, initialIndex = 0 }) => {
	const dispatch = useDispatch();
	const [currentIndex, setCurrentIndex] = useState(initialIndex);
	const [photoUrls, setPhotoUrls] = useState({});
	const [loading, setLoading] = useState(false);
	const safePhotos = Array.isArray(photos) ? photos : [];

	const resolvedPhotos = useMemo(() => {
		if (safePhotos.length === 0) return [];

		return safePhotos.map((photo) => ({
			...photo,
			resolvedUrl: (photo.fileKey && photoUrls[photo.fileKey]) || photo.url || photo.thumbnail,
		}));
	}, [safePhotos, photoUrls]);

	const missingFileKeys = useMemo(() => {
		if (safePhotos.length === 0) return [];

		const seen = new Set();
		return safePhotos
			.map((photo) => photo.fileKey)
			.filter((key) => {
				if (!key || photoUrls[key] || seen.has(key)) return false;
				seen.add(key);
				return true;
			});
	}, [safePhotos, photoUrls]);

	const totalPhotos = resolvedPhotos.length;
	const hasMultiplePhotos = totalPhotos > 1;

	useEffect(() => {
		setCurrentIndex(initialIndex);
	}, [initialIndex]);

	useEffect(() => {
		if (totalPhotos === 0) return;
		if (currentIndex > totalPhotos - 1) {
			setCurrentIndex(totalPhotos - 1);
		}
	}, [totalPhotos, currentIndex]);

	useEffect(() => {
		if (!open) {
			setLoading(false);
			return undefined;
		}

		if (missingFileKeys.length === 0) {
			setLoading(false);
			return undefined;
		}

		let isCancelled = false;
		setLoading(true);

		dispatch(getDefectPhotos({ fileKeys: missingFileKeys }))
			.unwrap()
			.then((urls) => {
				if (!isCancelled && urls) {
					setPhotoUrls((prev) => ({
						...prev,
						...urls,
					}));
				}
			})
			.catch((error) => {
				if (!isCancelled) {
					console.error('[PhotoGalleryModal] Failed to fetch defect photos', error);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [open, missingFileKeys, dispatch]);

	useEffect(() => {
		if (!open || totalPhotos === 0) {
			return;
		}

		const handleKeyDown = (e) => {
			if (!open) return;

			if (e.key === 'ArrowLeft' && hasMultiplePhotos) {
				setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalPhotos - 1));
			} else if (e.key === 'ArrowRight' && hasMultiplePhotos) {
				setCurrentIndex((prev) => (prev < totalPhotos - 1 ? prev + 1 : 0));
			} else if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [open, totalPhotos, hasMultiplePhotos, onClose]);

	if (!open) return null;

	const handlePrevious = () => {
		if (totalPhotos === 0) return;
		setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalPhotos - 1));
	};

	const handleNext = () => {
		if (totalPhotos === 0) return;
		setCurrentIndex((prev) => (prev < totalPhotos - 1 ? prev + 1 : 0));
	};

	const currentPhoto = resolvedPhotos[currentIndex] || resolvedPhotos[0];
	const currentPhotoSrc = currentPhoto?.resolvedUrl || '';
	const showSpinner = loading || totalPhotos === 0;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			className="photo-gallery-modal"
			PaperProps={{
				style: {
					backgroundColor: 'rgba(0, 0, 0, 0.95)',
					boxShadow: 'none',
					width: '95vw',
					height: '95vh',
				},
			}}
		>
			<DialogContent className="photo-gallery-content">
				<IconButton
					className="close-button"
					onClick={onClose}
					aria-label="close"
				>
					<CloseIcon />
				</IconButton>

				{showSpinner ? (
					<Box className="photo-loading-state">
						<CircularProgress color="primary" />
					</Box>
				) : (
					<>
						<Box className="photo-container">
							{hasMultiplePhotos && (
								<IconButton
									className="nav-button prev-button"
									onClick={handlePrevious}
									aria-label="previous photo"
								>
									<NavigateBeforeIcon />
								</IconButton>
							)}

							<Box className="photo-wrapper">
								<img
									src={currentPhotoSrc}
									alt={`Photo ${currentIndex + 1} of ${totalPhotos}`}
									className="gallery-photo"
								/>
							</Box>

							{hasMultiplePhotos && (
								<IconButton
									className="nav-button next-button"
									onClick={handleNext}
									aria-label="next photo"
								>
									<NavigateNextIcon />
								</IconButton>
							)}
						</Box>

						{hasMultiplePhotos && (
							<Box className="photo-counter">
								{currentIndex + 1} / {totalPhotos}
							</Box>
						)}
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default PhotoGalleryModal;
